/**
 * Sections, roles and audience masks — the single source of truth for "who may see what".
 * Used by both the server (SQL filters, media route, manifest) and the client (UI chips).
 *
 * Audience bits: 1 = whole choir (incl. conductor & musicians), then one bit per section.
 */

export const SECTIONS = ['T1', 'T2', 'B1', 'B2', 'conductor', 'musician'] as const;
export type Section = (typeof SECTIONS)[number];

export const SINGING_SECTIONS = ['T1', 'T2', 'B1', 'B2'] as const satisfies readonly Section[];

export const ROLES = ['member', 'editor', 'admin'] as const;
export type Role = (typeof ROLES)[number];

export const AUDIENCE_ALL = 1;
export const SECTION_BIT: Record<Section, number> = {
  T1: 2,
  T2: 4,
  B1: 8,
  B2: 16,
  conductor: 32,
  musician: 64,
};
export const AUDIENCE_MAX = 127;

export type AudienceGroup = 'all' | Section;
export const AUDIENCE_GROUPS: readonly AudienceGroup[] = ['all', ...SECTIONS];

export function isSection(v: unknown): v is Section {
  return typeof v === 'string' && (SECTIONS as readonly string[]).includes(v);
}

export function isRole(v: unknown): v is Role {
  return typeof v === 'string' && (ROLES as readonly string[]).includes(v);
}

export function groupBit(g: AudienceGroup): number {
  return g === 'all' ? AUDIENCE_ALL : SECTION_BIT[g];
}

export function maskFromGroups(groups: readonly AudienceGroup[]): number {
  let m = 0;
  for (const g of groups) m |= groupBit(g);
  return m;
}

export function groupsFromMask(mask: number): AudienceGroup[] {
  return AUDIENCE_GROUPS.filter((g) => (mask & groupBit(g)) !== 0);
}

export function isValidMask(mask: unknown): mask is number {
  return typeof mask === 'number' && Number.isInteger(mask) && mask >= 1 && mask <= AUDIENCE_MAX;
}

export function roleAtLeast(role: Role, min: Role): boolean {
  return ROLES.indexOf(role) >= ROLES.indexOf(min);
}

export interface AudienceSubject {
  role: Role;
  section: Section | null;
}

/** Bits that match this user's own audience (ignores editor privileges). */
export function ownMatchBits(section: Section | null): number {
  return AUDIENCE_ALL | (section ? SECTION_BIT[section] : 0);
}

/** Does the mask target the user's own section/whole choir? (member-facing view, no editor bypass) */
export function matchesOwnAudience(mask: number, section: Section | null): boolean {
  return (mask & ownMatchBits(section)) !== 0;
}

/** Strict visibility: editors/admins see everything; everyone else only their audience. */
export function isVisibleTo(mask: number, user: AudienceSubject): boolean {
  return roleAtLeast(user.role, 'editor') || matchesOwnAudience(mask, user.section);
}

/**
 * Specificity score used to pick a default file for a member: a file aimed at the
 * user's own section beats a whole-choir file; files not aimed at the user score 0.
 */
export function audienceSpecificity(mask: number, section: Section | null): number {
  if (section && (mask & SECTION_BIT[section]) !== 0) return 2;
  if ((mask & AUDIENCE_ALL) !== 0) return 1;
  return 0;
}

/** Sections whose members are counted in attendance statistics by default. */
export function attendanceCountsSection(section: Section | null, eventMask: number): boolean {
  if (!section) return false;
  if ((SINGING_SECTIONS as readonly string[]).includes(section)) {
    return (eventMask & (AUDIENCE_ALL | SECTION_BIT[section])) !== 0;
  }
  // Conductor / musicians only when the event explicitly targets them.
  return (eventMask & SECTION_BIT[section]) !== 0;
}
